import React, { useContext } from "react";
import AuthContext from "../../hooks/authContext";
import { useTour } from "../ui/TourContext";
import { useNavigate } from "react-router";
import { ShieldAlert, Loader2 } from "lucide-react";

const Unauthorized = () => {
  const auth = useContext(AuthContext);
  const { stopTour } = useTour();
  const navigate = useNavigate();

  const handleLogoutAndRedirect = () => {
    try {
      auth.logoutuser?.();
      stopTour?.();
      document.documentElement.classList.remove("dark");
      localStorage.removeItem("clientId");
      localStorage.removeItem("companyId");
      localStorage.removeItem("clients");
      localStorage.removeItem("companies");
      localStorage.setItem("darkMode", "false");
      if (auth?.userProfile?.clientsLength === 0 && auth?.userProfile?.employeesLength === 0) {
        localStorage.removeItem("hasSeenTour");
      } else {
        localStorage.setItem("hasSeenTour", "true");
      }
      navigate("/login");
    } catch (err) {
      console.error("Logout cleanup failed:", err);
    }
  };

  return (
    <div
      className="min-h-[100dvh] flex flex-col items-center justify-center px-4 sm:px-6 py-10 sm:py-14 bg-gradient-to-b from-slate-100 to-slate-200/80 dark:from-[#0f1419] dark:to-[#1A1F2C]"
      data-page="unauthorized"
    >
      {auth?.isLoading === true ? (
        <div
          className="flex flex-col items-center gap-4 text-slate-500 dark:text-slate-400"
          role="status"
          aria-live="polite"
        >
          <Loader2 className="h-10 w-10 sm:h-12 sm:w-12 animate-spin text-slate-400 dark:text-slate-500" />
          <p className="text-sm sm:text-base font-medium">Checking your access…</p>
        </div>
      ) : (
        <div className="w-full max-w-md sm:max-w-lg">
          <div
            className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white/90 shadow-lg shadow-slate-900/5 backdrop-blur-sm dark:border-slate-700/80 dark:bg-[#1e2530]/95 dark:shadow-black/40"
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 opacity-90" />
            <div className="px-5 pt-32 pb-32 sm:px-8 sm:pt-10 sm:pb-10 text-center">
              <div className="mx-auto mb-5 flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-2xl bg-amber-50 ring-1 ring-amber-200/60 dark:bg-amber-950/40 dark:ring-amber-800/50">
                <ShieldAlert
                  className="h-8 w-8 sm:h-10 sm:w-10 text-amber-600 dark:text-amber-400"
                  strokeWidth={1.75}
                  aria-hidden
                />
              </div>
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                Access restricted
              </h1>
              <p className="mt-12 text-sm sm:text-base leading-relaxed text-slate-600 dark:text-slate-300">
                You don&apos;t have permission to view this page. It may require a different role,
                or the link could be incorrect.
              </p>
              <p className="mt-8 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                If you believe this is a mistake, contact your administrator.
              </p>
              {auth?.status !== true && (
                <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:justify-center sm:items-center">
                  <button
                    type="button"
                    onClick={handleLogoutAndRedirect}
                    className="w-full sm:w-auto inline-flex items-center justify-center rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 dark:focus-visible:ring-offset-[#1e2530]"
                  >
                    Sign in with another account
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Unauthorized;
